import React, { useState } from "react";
import {
  ConfirmationStep,
  Threedsv2ChallengeStep,
} from "./Steps";
import {
  FormPaymentRequest,
  GlobalpayPayment3DSRequiredResponse,
  GlobalpayPaymentResponse,
  Threedsv2ChallengeResponse,
} from "./types";
import EnterCardDetailsStep from "./EnterCardDetailsStep";

export interface CardPaymentFlowProps {
  form: FormPaymentRequest;
  submitPayment: (
    form: FormPaymentRequest
  ) => Promise<GlobalpayPaymentResponse>;
  continuePayment: (
    response: Threedsv2ChallengeResponse
  ) => Promise<GlobalpayPaymentResponse>;
  setForm: React.Dispatch<React.SetStateAction<FormPaymentRequest>>;
  onBack: () => void;
  onComplete: (response: GlobalpayPaymentResponse) => void;
}

type CardPaymentFlowStep =
  | "cardDetails"
  | "confirmPayment"
  | "3DSv2Challenge";

export const CardPaymentFlow: React.FC<CardPaymentFlowProps> = ({
  form,
  submitPayment,
  continuePayment,
  setForm,
  onComplete,
  onBack,
}) => {
  const [step, setStep] = useState<CardPaymentFlowStep>("cardDetails");
  const [submitting, setSubmitting] = useState(false);
  const [makePaymentResponse, setMakePaymentResponse] =
    useState<GlobalpayPaymentResponse | null>(null);

  const handleSubmitPayment = async () => {
    setSubmitting(true);
    const resp = await submitPayment(form);
    setMakePaymentResponse(resp);
    setSubmitting(false);
    if (resp.status === "ExtendedAuthorisationRequired") {
      setStep("3DSv2Challenge");
    } else {
      onComplete(resp);
    }
  };

  const handleCompletePayment = async (cres: string) => {
    if (!makePaymentResponse) {
      console.error("No response to continue payment");
      return;
    }
    if (makePaymentResponse.status !== "ExtendedAuthorisationRequired") {
      console.error("Cannot continue payment, not in 3DSv2 challenge state");
      return;
    }
    const resp = await continuePayment({
      cres,
      securityTransId: makePaymentResponse.securityTransId!,
      securityDataVersion: makePaymentResponse.securityDataVersion!,
      encryptedOriginalRequest: makePaymentResponse.encryptedOriginalRequest!,
    });
    onComplete(resp);
  };

  return (
    <>
      {step === "cardDetails" && (
        <EnterCardDetailsStep
          form={form}
          setForm={setForm}
          onNext={() => setStep("confirmPayment")}
          onBack={onBack}
        />
      )}
      {step === "confirmPayment" && (
        <ConfirmationStep
          form={form}
          onBack={() => setStep("cardDetails")}
          onSubmit={handleSubmitPayment}
          submitting={submitting}
        />
      )}
      {step === "3DSv2Challenge" && !!makePaymentResponse && (
        <Threedsv2ChallengeStep
          onChallengeComplete={handleCompletePayment}
          challengeRequiredResponse={
            makePaymentResponse as GlobalpayPayment3DSRequiredResponse
          }
        />
      )}
    </>
  );
};
